import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
	useParentLoginMutation,
	useParentChangePasswordMutation,
} from '../../store/api/authApi';
import { useAppDispatch } from '../../store/hooks';
import { setCredentials, completedSetup } from '../../store/authSlice';
import { User, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import alforaaFullLogoDark from '../../assets/svg/logo/alforaa_full_logo_dark.svg';
import { ButtonSpinner } from '../../components/shared/SharedComponents';
import { analytics, identifyUser } from '../../services/analytics';
import { usePageView } from '../../hooks/usePageView';
import { EVENTS } from '../../constants/analyticsEvents';

function AuroraShellTwoPane({
	eyebrow,
	headlineTop,
	headlineBottom,
	subline,
	footerTitle,
	footerSub,
	children,
	testId,
}: {
	eyebrow: string;
	headlineTop: string;
	headlineBottom: string;
	subline: string;
	footerTitle: string;
	footerSub: string;
	children: React.ReactNode;
	testId: string;
}) {
	return (
		<div
			className='min-h-screen aurora-bg flex flex-col lg:flex-row relative overflow-hidden'
			data-testid={testId}>
			<div
				aria-hidden
				className='pointer-events-none absolute top-[-100px] left-[100px] w-[500px] h-[500px] rounded-full'
				style={{
					background:
						'radial-gradient(circle, rgba(255,180,140,0.55), transparent 70%)',
					filter: 'blur(20px)',
				}}
			/>
			<div
				aria-hidden
				className='pointer-events-none absolute bottom-[-100px] right-[100px] w-[500px] h-[500px] rounded-full'
				style={{
					background:
						'radial-gradient(circle, rgba(220,200,255,0.6), transparent 70%)',
					filter: 'blur(20px)',
				}}
			/>

			<div className='lg:hidden flex items-center justify-center pt-8 pb-2 px-6 relative z-10'>
				<img
					src={alforaaFullLogoDark}
					alt='Alforaa'
					className='h-9 w-auto block'
				/>
			</div>

			<div className='hidden lg:flex flex-1 flex-col justify-between p-14 relative z-10'>
				<div className='flex items-center'>
					<img
						src={alforaaFullLogoDark}
						alt='Alforaa'
						className='h-10 w-auto block'
					/>
				</div>
				<div>
					<div className='text-[14px] text-aurora-coral font-semibold uppercase tracking-[0.04em]'>
						{eyebrow}
					</div>
					<div className='text-[56px] font-semibold tracking-[-0.04em] text-aurora-ink leading-[1.05] mt-3'>
						{headlineTop}
						<br />
						<span
							style={{
								background: 'linear-gradient(135deg, #FF7A45, #E8541F)',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent',
							}}>
							{headlineBottom}
						</span>
					</div>
					<div className='text-[16px] text-aurora-ink-soft leading-relaxed mt-4 max-w-[460px]'>
						{subline}
					</div>
				</div>
				<div className='flex gap-5 items-center'>
					<div className='flex'>
						{[
							'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=160&h=160&fit=crop&crop=faces&q=80',
							'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=160&h=160&fit=crop&crop=faces&q=80',
							'https://images.unsplash.com/photo-1577896851231-70ef18881754?w=160&h=160&fit=crop&crop=faces&q=80',
							'https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=160&h=160&fit=crop&crop=faces&q=80',
						].map((src, i) => (
							<img
								key={i}
								src={src}
								alt=''
								className='w-9 h-9 rounded-full border-2 border-white object-cover bg-aurora-coral-soft'
								style={{ marginLeft: i === 0 ? 0 : -10 }}
							/>
						))}
					</div>
					<div className='text-[12px] text-aurora-ink-soft'>
						<div className='font-semibold text-aurora-ink'>{footerTitle}</div>
						<div>{footerSub}</div>
					</div>
				</div>
			</div>

			<div className='flex-1 flex items-center justify-center relative z-10 p-6 sm:p-10 lg:p-0'>
				{children}
			</div>
		</div>
	);
}

export function ParentChangePasswordPage() {
	useAuth();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [changePassword] = useParentChangePasswordMutation();
	const [oldPassword, setOldPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showOld, setShowOld] = useState(false);
	const [showNew, setShowNew] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		if (newPassword !== confirmPassword) {
			setError('New passwords do not match');
			return;
		}
		if (newPassword.length < 8) {
			setError('New password must be at least 8 characters');
			return;
		}
		setLoading(true);
		try {
			await changePassword({ oldPassword, newPassword }).unwrap();
			dispatch(completedSetup());
			navigate('/parent/dashboard');
		} catch (e: any) {
			setError(e?.data?.detail || 'Failed to change password');
		}
		setLoading(false);
	};

	return (
		<AuroraShellTwoPane
			eyebrow='Secure your account'
			headlineTop='One quick step,'
			headlineBottom='then you’re in.'
			subline='Set a new password to replace the temporary one. You’ll use it every time you sign in.'
			footerTitle='Your account, your choice'
			footerSub='Pick something memorable but private.'
			testId='parent-change-password-page'>
			<form
				onSubmit={handleSubmit}
				className='aurora-glass-strong rounded-[28px] p-7 sm:p-10 w-full max-w-[440px] animate-fade-in'>
				<div className='text-[26px] font-semibold text-aurora-ink tracking-[-0.025em]'>
					Set your password
				</div>
				<div className='text-[13px] text-aurora-ink-mute mt-1'>
					Change your temporary password to continue.
				</div>

				<div className='mt-6'>
					<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
						Current password
					</label>
					<div className='relative'>
						<Lock
							size={16}
							className='absolute left-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint'
						/>
						<input
							type={showOld ? 'text' : 'password'}
							value={oldPassword}
							onChange={(e) => setOldPassword(e.target.value)}
							placeholder='Your temporary password'
							required
							className='w-full pl-10 pr-10 py-3 rounded-[12px] bg-white border-2 border-aurora-ink/20 shadow-[0_1px_2px_rgba(15,18,53,0.05)] text-[13.5px] text-aurora-ink placeholder:text-aurora-ink-faint focus:outline-none focus:ring-2 focus:ring-aurora-coral/40 focus:border-aurora-coral transition-all'
						/>
						<button
							type='button'
							onClick={() => setShowOld((v) => !v)}
							className='absolute right-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint hover:text-aurora-ink'>
							{showOld ? <EyeOff size={16} /> : <Eye size={16} />}
						</button>
					</div>
				</div>

				<div className='mt-4'>
					<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
						New password
					</label>
					<div className='relative'>
						<Lock
							size={16}
							className='absolute left-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint'
						/>
						<input
							type={showNew ? 'text' : 'password'}
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							placeholder='Min. 8 characters'
							required
							className='w-full pl-10 pr-10 py-3 rounded-[12px] bg-white border-2 border-aurora-ink/20 shadow-[0_1px_2px_rgba(15,18,53,0.05)] text-[13.5px] text-aurora-ink placeholder:text-aurora-ink-faint focus:outline-none focus:ring-2 focus:ring-aurora-coral/40 focus:border-aurora-coral transition-all'
						/>
						<button
							type='button'
							onClick={() => setShowNew((v) => !v)}
							className='absolute right-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint hover:text-aurora-ink'>
							{showNew ? <EyeOff size={16} /> : <Eye size={16} />}
						</button>
					</div>
				</div>

				<div className='mt-4'>
					<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
						Confirm new password
					</label>
					<div className='relative'>
						<Lock
							size={16}
							className='absolute left-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint'
						/>
						<input
							type={showConfirm ? 'text' : 'password'}
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							placeholder='Repeat new password'
							required
							className='w-full pl-10 pr-10 py-3 rounded-[12px] bg-white border-2 border-aurora-ink/20 shadow-[0_1px_2px_rgba(15,18,53,0.05)] text-[13.5px] text-aurora-ink placeholder:text-aurora-ink-faint focus:outline-none focus:ring-2 focus:ring-aurora-coral/40 focus:border-aurora-coral transition-all'
						/>
						<button
							type='button'
							onClick={() => setShowConfirm((v) => !v)}
							className='absolute right-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint hover:text-aurora-ink'>
							{showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
						</button>
					</div>
				</div>

				{error && (
					<p className='text-status-error text-[12px] text-center mt-4'>
						{error}
					</p>
				)}

				<button
					type='submit'
					disabled={loading}
					className='w-full py-3 mt-5 rounded-[12px] bg-coral-gradient text-white text-[14px] font-semibold shadow-coral hover:opacity-95 disabled:opacity-50 transition-all flex items-center justify-center gap-2'>
					{loading && <ButtonSpinner />}
					{loading ? 'Saving…' : 'Set password & continue'}
					{!loading && <ArrowRight size={16} />}
				</button>
			</form>
		</AuroraShellTwoPane>
	);
}

export default function ParentLoginPage() {
	usePageView(EVENTS.parent.LOGIN_PAGE_VIEWED);
	const navigate = useNavigate();
	useAuth();
	const dispatch = useAppDispatch();
	const [parentLogin, { isLoading: loading }] = useParentLoginMutation();
	const [identifier, setIdentifier] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState('');

	// Parents can sign in with an email OR an Alforaa ID. If the identifier
	// contains '@' we require valid email shape; otherwise accept any non-empty
	// identifier (the server resolves it to a parent record).
	const trimmedId = identifier.trim();
	const identifierLooksLikeEmail = trimmedId.includes('@');
	const identifierValid = identifierLooksLikeEmail
		? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedId)
		: trimmedId.length > 0;
	const canSubmit = identifierValid && password.length > 0 && !loading;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		if (!canSubmit) return;
		analytics.parent.loginPinSubmitted();
		try {
			const result = await parentLogin({
				identifier: trimmedId,
				password,
			}).unwrap();
			identifyUser(result.user, 'parent');
			analytics.parent.loginSucceeded();
			dispatch(
				setCredentials({
					user: result.user,
					role: 'parent',
					accessToken: result.accessToken,
					refreshToken: result.refreshToken,
					mustChangePassword: result.mustChangePassword || false,
				}),
			);
			if (result.mustChangePassword) {
				navigate('/parent/change-password');
			} else {
				navigate('/parent/dashboard');
			}
		} catch (e: any) {
			const rawReason = (e as any)?.data?.detail || '';
			setError('Email or password is incorrect');
			analytics.parent.loginPinFailed(rawReason || 'invalid_credentials');
		}
	};

	return (
		<AuroraShellTwoPane
			eyebrow='For parents'
			headlineTop='Follow every step'
			headlineBottom='of their journey.'
			subline='See your child’s curriculum progress, session highlights, and what’s coming up next — all in one place.'
			footerTitle='Stay close to the learning'
			footerSub='Real updates, no guesswork.'
			testId='parent-login-page'>
			<form
				onSubmit={handleSubmit}
				className='aurora-glass-strong rounded-[28px] p-7 sm:p-10 w-full max-w-[440px] animate-fade-in'>
				<div className='text-[26px] font-semibold text-aurora-ink tracking-[-0.025em]'>
					Welcome back
				</div>
				<div className='text-[13px] text-aurora-ink-mute mt-1'>
					Sign in to your parent dashboard
				</div>

				<div className='mt-6'>
					<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
						Email or Alforaa ID
					</label>
					<div className='relative'>
						<User
							size={16}
							className='absolute left-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint'
						/>
						<input
							type='text'
							value={identifier}
							onChange={(e) => setIdentifier(e.target.value)}
							placeholder='parent@example.com or 26-IN-...'
							data-testid='parent-identifier-input'
							className='w-full pl-10 pr-4 py-3 rounded-[12px] bg-white border-2 border-aurora-ink/20 shadow-[0_1px_2px_rgba(15,18,53,0.05)] text-[13.5px] text-aurora-ink placeholder:text-aurora-ink-faint focus:outline-none focus:ring-2 focus:ring-aurora-coral/40 focus:border-aurora-coral transition-all'
						/>
					</div>
				</div>

				<div className='mt-4'>
					<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
						Password
					</label>
					<div className='relative'>
						<Lock
							size={16}
							className='absolute left-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint'
						/>
						<input
							type={showPassword ? 'text' : 'password'}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder='Enter your password'
							data-testid='parent-password-input'
							className='w-full pl-10 pr-10 py-3 rounded-[12px] bg-white border-2 border-aurora-ink/20 shadow-[0_1px_2px_rgba(15,18,53,0.05)] text-[13.5px] text-aurora-ink placeholder:text-aurora-ink-faint focus:outline-none focus:ring-2 focus:ring-aurora-coral/40 focus:border-aurora-coral transition-all'
						/>
						<button
							type='button'
							onClick={() => setShowPassword(!showPassword)}
							className='absolute right-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint hover:text-aurora-ink'>
							{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
						</button>
					</div>
				</div>

				{error && (
					<p
						className='text-status-error text-[12px] text-center mt-4'
						data-testid='parent-login-error'>
						{error}
					</p>
				)}

				<button
					type='submit'
					disabled={!canSubmit}
					data-testid='parent-login-submit'
					className='w-full py-3 mt-5 rounded-[12px] bg-coral-gradient text-white text-[14px] font-semibold shadow-coral hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2'>
					{loading && <ButtonSpinner />}
					{loading ? 'Signing in…' : 'Sign in'}
					{!loading && <ArrowRight size={16} />}
				</button>
			</form>
		</AuroraShellTwoPane>
	);
}
